import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, ArrowUp, ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/lang";
import { bookingHref } from "@/content";
import { LogoMark } from "./Logo";

const API = process.env.REACT_APP_BACKEND_URL;
const T = {
  ro: { title: "Asistent Extra Value", sub: "Răspunde imediat · AI", hello: "Bună! Sunt asistentul Extra Value. Te pot ajuta cu informații despre servicii, modul de lucru sau onorariu.", ph: "Scrie o întrebare…", err: "Momentan nu pot răspunde. Scrie-ne la office@extravalue.ro.", limit: "Ai atins limita de mesaje. Programează o discuție sau scrie-ne la office@extravalue.ro.", book: "Programează 30 min", open: "Deschide asistentul", sug: ["Cum funcționează onorariul?", "Ce include analiza energiei?", "Cum decurge analiza?"] },
  en: { title: "Extra Value assistant", sub: "Instant answers · AI", hello: "Hi! I'm the Extra Value assistant. Ask me about our services, process or fee model.", ph: "Ask a question…", err: "I can't answer right now. Write to us at office@extravalue.ro.", limit: "Message limit reached. Book a call or write to office@extravalue.ro.", book: "Book 30 min", open: "Open assistant", sug: ["How does the fee work?", "What does the energy analysis cover?", "How does the analysis work?"] },
};

const sessionId = () => {
  let id = localStorage.getItem("ev-chat-session");
  if (!id) { id = (crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`).replace(/[^a-zA-Z0-9-]/g, ""); localStorage.setItem("ev-chat-session", id); }
  return id;
};

const Bubble = ({ m, i }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${i}`}>
    <p className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "rounded-br-md bg-gold text-bg" : "rounded-bl-md border border-line bg-surface text-ink"}`}>
      {m.content || <span className="inline-flex gap-1"><span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" /></span>}
    </p>
  </div>
);

export const ChatWidget = () => {
  const { lang } = useLang();
  const t = T[lang];
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!open || loaded) return;
    setLoaded(true);
    fetch(`${API}/api/chat/history?session_id=${sessionId()}`).then((r) => (r.ok ? r.json() : [])).then((d) => setMsgs(Array.isArray(d) ? d : [])).catch(() => {});
  }, [open, loaded]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs, open]);

  const setLast = (fn) => setMsgs((p) => [...p.slice(0, -1), fn(p[p.length - 1])]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput(""); setBusy(true);
    setMsgs((p) => [...p, { role: "user", content: q }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/api/chat/stream`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sessionId(), message: q.slice(0, 1000) }) });
      if (!res.ok || !res.body) { setLast((m) => ({ ...m, content: res.status === 429 ? t.limit : t.err })); return; }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const chunks = buf.split("\n\n"); buf = chunks.pop();
        for (const c of chunks) {
          if (!c.startsWith("data: ")) continue;
          const ev = JSON.parse(c.slice(6));
          if (ev.t) setLast((m) => ({ ...m, content: m.content + ev.t }));
          if (ev.error) setLast((m) => ({ ...m, content: m.content || t.err }));
        }
      }
    } catch { setLast((m) => ({ ...m, content: m.content || t.err })); }
    finally { setBusy(false); }
  };

  if (!API) return null;
  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.97 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} className="fixed inset-x-3 bottom-24 z-[70] flex h-[min(620px,calc(100svh-8rem))] flex-col overflow-hidden rounded-[24px] border border-line bg-bg shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)] sm:inset-x-auto sm:right-6 sm:w-[400px]" data-testid="chat-panel">
            <div className="flex items-center justify-between border-b border-line bg-surface px-5 py-4">
              <div className="flex items-center gap-3"><LogoMark size={30} /><div><p className="font-display text-sm font-semibold text-ink">{t.title}</p><p className="text-xs text-zinc-400">{t.sub}</p></div></div>
              <button onClick={() => setOpen(false)} className="rounded-full p-2 text-zinc-300 transition-colors hover:bg-surface-2 hover:text-ink" data-testid="chat-close-btn" aria-label="Close"><X size={18} /></button>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-5" data-lenis-prevent data-testid="chat-messages">
              <Bubble m={{ role: "assistant", content: t.hello }} i="hello" />
              {msgs.length === 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {t.sug.map((s, i) => <button key={s} onClick={() => send(s)} data-testid={`chat-suggestion-${i}`} className="rounded-full border border-line px-3 py-1.5 text-xs text-zinc-300 transition-colors hover:border-gold hover:text-ink">{s}</button>)}
                </div>
              )}
              {msgs.map((m, i) => <Bubble key={i} m={m} i={i} />)}
              <div ref={endRef} />
            </div>
            <div className="border-t border-line p-3">
              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2">
                <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1} maxLength={1000} placeholder={t.ph} data-testid="chat-input" className="max-h-28 min-h-[44px] flex-1 resize-none rounded-2xl border border-line bg-surface px-4 py-3 text-sm text-ink placeholder:text-zinc-400 focus:border-gold focus:outline-none" />
                <button type="submit" disabled={busy || !input.trim()} data-testid="chat-send-btn" aria-label="Send" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-gold text-bg transition-[opacity,transform] hover:scale-105 disabled:opacity-40"><ArrowUp size={18} /></button>
              </form>
              <a href={bookingHref()} target="_blank" rel="noopener noreferrer" data-testid="chat-book-link" className="mt-2 flex items-center justify-center gap-1 py-1 text-xs text-gold hover:underline">{t.book} <ArrowUpRight size={12} /></a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.button initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 1.8, type: "spring", stiffness: 220, damping: 18 }} onClick={() => setOpen(!open)} aria-label={t.open} data-testid="chat-toggle-btn" className="fixed bottom-5 right-5 z-[70] grid h-14 w-14 place-items-center rounded-full bg-gold text-bg shadow-[0_18px_50px_-12px_rgba(232,176,75,0.7)] transition-transform hover:scale-105 sm:bottom-6 sm:right-6">
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </motion.button>
    </>
  );
};
